
import { BaseEntity } from '../BaseEntity';
import type { LevelRuntime } from '../../engine/gameState';
import { resolveAllPlatforms, hasFloorAhead } from '../../engine/collision';
import { GRAVITY, MAX_FALL_SPEED } from '../../engine/physics';
import Logger from '../../utils/logger';

export type NibblerState = 'PATROL' | 'SQUISHED' | 'DEAD';

/** Width/height of the Nibbler hitbox in logical pixels. */
export const NIBBLER_WIDTH = 24;
export const NIBBLER_HEIGHT = 24;

/** Walk speed in logical pixels per second. */
const PATROL_SPEED = 60;

/** Duration of the squish death animation in seconds. */
const SQUISH_DURATION = 0.5;
/** Horizontal slide speed during squish (px/s). */
const SQUISH_SLIDE_SPEED = 40;

/** Render colour for greybox. */
export const NIBBLER_COLOR = '#e87a30'; // orange

// DECISION: Reusable edge-detection box to avoid per-tick allocations.
// DECISION: Reusable hitbox for collision resolution to avoid per-tick allocations.
const _hitbox = { x: 0, y: 0, width: NIBBLER_WIDTH, height: NIBBLER_HEIGHT };

/**
 * Nibbler — ground-patrol enemy.
 *
 * Spawn with `new Nibbler(x, y)`. Call `update(dt, level)` each fixed tick.
 * Read `dead` to know when to remove from the scene.
 */
export class Nibbler extends BaseEntity {
  public readonly entityType = 'Nibbler';
  public readonly color = NIBBLER_COLOR;
  /**
   * Measured empty air above Nibbler's art (6.91px of its 24px box). Floored
   * so the inset never eats real pixels. Verified by enemyCombatInset.test.ts.
   */
  public readonly combatTopInset = 6;
  public state: NibblerState = 'PATROL';

  /**
   * True once a stomp has started the squish animation (`SQUISHED`) or the
   * death sequence has finished (`DEAD`). See `Enemy.isDying`.
   */
  public get isDying(): boolean {
    return this.state === 'SQUISHED' || this.state === 'DEAD';
  }
  /** True when facing right (used by renderer for sprite flip). */
  public facingRight = true;
  /** Normalised squish progress 0→1 (used by renderer for scale animation). */
  public squishProgress = 0;
  /** Horizontal direction of the squish slide (+1 or -1). */
  private _squishDir = 1;
  private _squishTimer = 0;

  constructor(x: number, y: number) {
    super(x, y, NIBBLER_WIDTH, NIBBLER_HEIGHT);
    this.vx = PATROL_SPEED; // start walking right
  }

  public update(dt: number, level: LevelRuntime): void {
    if (this.state === 'DEAD') return;

    if (this.state === 'SQUISHED') {
      this._squishTimer -= dt;
      this.squishProgress = 1 - Math.max(0, this._squishTimer) / SQUISH_DURATION;
      // Slide forward during squish
      this.x += this._squishDir * SQUISH_SLIDE_SPEED * dt;
      if (this._squishTimer <= 0) {
        this.state = 'DEAD';
        this.dead = true;
        Logger.debug('Nibbler', 'Squish complete');
      }
      return;
    }

    // ── Gravity ───────────────────────────────────────────────────────────────
    this.vy += GRAVITY * dt;
    this.vy = Math.min(this.vy, MAX_FALL_SPEED);

    // ── Integrate ─────────────────────────────────────────────────────────────
    this.x += this.vx * dt;
    this.y += this.vy * dt;

    // ── Collision resolution ──────────────────────────────────────────────────
    // DECISION: Update reusable hitbox in-place instead of calling getHitbox().
    _hitbox.x = this.x;
    _hitbox.y = this.y;
    const result = resolveAllPlatforms(this.x, this.y, _hitbox, level.platforms, this.vy);
    this.x = result.x;
    this.y = result.y;

    if (result.landedOnTop) {
      this.vy = 0;
      // Edge detection: peek one step ahead — if the tile in front has no floor,
      // turn around before stepping off.
      const hasFloor = hasFloorAhead(
        this.x,
        this.y,
        this.width,
        this.height,
        this.vx,
        level.platforms,
      );
      if (!hasFloor) {
        this.vx = -this.vx;
        this.facingRight = this.vx > 0;
        Logger.debug('Nibbler', 'Turn at edge');
      }
    }

    if (result.hitLeft || result.hitRight) {
      this.vx = -this.vx;
      this.facingRight = this.vx > 0;
      Logger.debug('Nibbler', 'Turn at wall');
    }
  }

  /**
   * Apply one hit (stomp or shard). Nibblers die in one hit.
   * Stomp triggers squish animation; shard kills instantly.
   * @param fromStomp - True if the hit came from a stomp (plays squish anim).
   * @returns true if the enemy was killed by this hit (false if already dead).
   */
  public applyHit(fromStomp = false): boolean {
    if (this.state === 'DEAD' || this.state === 'SQUISHED') return false;
    if (fromStomp) {
      this.state = 'SQUISHED';
      this.vx = 0;
      this._squishTimer = SQUISH_DURATION;
      this.squishProgress = 0;
      // DECISION: Slide in the Nibbler's current walk direction on squish.
      // facingRight tracks the Nibbler's own walk direction; Nova's position
      // is not available here, so we use the Nibbler's facing as a proxy.
      this._squishDir = this.facingRight ? 1 : -1;
      Logger.debug('Nibbler', 'Squished by stomp');
      return true;
    }
    this.state = 'DEAD';
    this.dead = true;
    Logger.debug('Nibbler', 'Killed by shard');
    return true;
  }
}
